import { z } from 'zod';

/**
 * Content and save-blob schemas. Content is validated once at load so a bad
 * hand-authored wave fails loudly at the boundary instead of producing a
 * confusing sim state forty seconds into a level.
 */

const temperament = z.object({
  restlessness: z.number().min(0).max(1),
  jitter: z.number().min(0).max(1),
  reactivity: z.number().min(0).max(1),
  pulsePeriod: z.number().positive(),
  traits: z.array(z.string()).min(1),
});

const palette = z.object({
  halo: z.number().int(),
  crust: z.number().int(),
  crustDark: z.number().int(),
  crustLight: z.number().int(),
  feature: z.number().int(),
  featureDark: z.number().int(),
  featureLight: z.number().int(),
  core: z.number().int(),
  coreRim: z.number().int(),
  accent: z.number().int(),
});

const body = z.object({
  feature: z.enum(['shards', 'crystals', 'cells', 'plates', 'vents', 'lattice']),
  featureCount: z.number().int().min(1).max(64),
  featureReach: z.number().min(0).max(1),
  featureChaos: z.number().min(0).max(1),
  coreRatio: z.number().min(0.1).max(0.9),
  shellWobble: z.number().min(0).max(0.5),
  shellLobes: z.number().int().min(2).max(24),
  speckle: z.number().min(0).max(1),
  halo: z.number().min(0).max(0.6),
  bands: z.number().int().min(0).max(12),
  face: z.enum(['calm', 'fierce', 'sleepy', 'machine', 'ancient', 'hollow']),
});

const passive = z.object({
  id: z.string(),
  label: z.string(),
  text: z.string(),
  juiceBonus: z.number().optional(),
  surfaceDamageMult: z.number().optional(),
  ringDamageMult: z.number().optional(),
  enemySpeedMult: z.number().optional(),
  ringSpeedMult: z.number().optional(),
  ringRadiusBonus: z.number().optional(),
  extraRingSlots: z.number().int().optional(),
  noSurfaceSlots: z.boolean().optional(),
});

const ultimate = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['pulse', 'cone', 'freeze', 'swarm', 'singularity', 'ringstorm']),
  cooldown: z.number().positive(),
  aimed: z.boolean(),
  text: z.string(),
  damage: z.number().min(0),
  spread: z.number().min(0),
  duration: z.number().min(0),
  groundDuration: z.number().min(0),
  groundDps: z.number().min(0),
  count: z.number().int().min(1),
  knockback: z.number().min(0),
  frontSpeed: z.number().min(0),
});

const evolutionChoice = z.object({
  id: z.string(),
  name: z.string(),
  text: z.string(),
  ultimate: ultimate.partial().optional(),
  passive: passive.partial().optional(),
  body: body.partial().optional(),
  palette: palette.partial().optional(),
});

export const planetSchema = z.object({
  id: z.string(),
  name: z.string(),
  designation: z.string(),
  epithet: z.string(),
  bio: z.string(),
  temperament,
  passive,
  ultimate,
  exclusive: z.string(),
  slots: z.object({
    surface: z.number().int().min(0).max(12),
    ring: z.number().int().min(0).max(10),
  }),
  spin: z.number(),
  ringInertia: z.number().positive(),
  coreHp: z.number().positive(),
  palette,
  body,
  voice: z.object({
    onWave: z.array(z.string()).min(1),
    onUltimate: z.array(z.string()).min(1),
    onHurt: z.array(z.string()).min(1),
    onClear: z.array(z.string()).min(1),
  }),
  evolutions: z.object({
    l4: z.array(evolutionChoice),
    l8: z.array(evolutionChoice),
  }),
});

export const enemySchema = z.object({
  id: z.string(),
  name: z.string(),
  behaviour: z.enum([
    'straight',
    'fan',
    'splitter',
    'shielded',
    'burrow',
    'carrier',
    'cloak',
    'feint',
    'armoured',
    'healer',
    'boss',
  ]),
  hp: z.number().positive(),
  speed: z.number().positive(),
  size: z.number().positive(),
  value: z.number().min(0),
  damage: z.number().min(0),
  stageMin: z.number().min(0),
  stageMax: z.number().min(0),
  colour: z.number().int(),
  splitInto: z.string().optional(),
  splitCount: z.number().int().optional(),
  shieldArc: z.number().optional(),
  shieldMult: z.number().optional(),
  armour: z.number().optional(),
  healRate: z.number().optional(),
  healArc: z.number().optional(),
  parkR: z.number().optional(),
  spawnEvery: z.number().optional(),
  spawns: z.string().optional(),
  cloakRadius: z.number().optional(),
  veerAt: z.number().optional(),
  veerSpread: z.number().optional(),
  phases: z.number().int().optional(),
});

const tier = z.object({
  cost: z.number().min(0),
  damage: z.number().min(0),
  rate: z.number().min(0),
  spread: z.number().min(0),
  range: z.number().min(0),
  muzzle: z.number().min(0),
  splash: z.number().min(0),
  moonArc: z.number().optional(),
  mass: z.number().optional(),
  auraMult: z.number().optional(),
  auraRadius: z.number().optional(),
  charges: z.number().int().optional(),
});

export const structureSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['turret', 'beam', 'moon', 'mine', 'support', 'drone-bay']),
  layer: z.enum(['surface', 'ring', 'both']),
  text: z.string(),
  colour: z.number().int(),
  radar: z.boolean().optional(),
  support: z.enum(['damage-aura', 'core-shield', 'core-repair']).optional(),
  exclusiveTo: z.string().optional(),
  jewelCost: z.number().int().min(0),
  mergesInto: z.string().optional(),
  mergeOnly: z.boolean().optional(),
  tiers: z.array(tier).min(1),
});

const waveGroup = z.object({
  enemy: z.string(),
  count: z.number().int().min(1),
  bearing: z.union([z.number(), z.literal('random')]),
  fan: z.number().min(0),
  at: z.number().min(0),
  gap: z.number().min(0),
  stage: z.number().min(0).optional(),
});

export const levelSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['standard', 'elite', 'boss', 'cache']),
  region: z.string(),
  modifiers: z.array(z.string()),
  startingJuice: z.number().min(0),
  structures: z.array(z.string()).optional(),
  mounts: z
    .object({ surface: z.number().int().min(0).optional(), ring: z.number().int().min(0).optional() })
    .optional(),
  upgrades: z.array(z.string()).optional(),
  startingUpgrades: z.record(z.string(), z.number().int().min(1)).optional(),
  waves: z.array(
    z.object({
      id: z.string(),
      groups: z.array(waveGroup),
      prep: z.number().min(0),
      reward: z.number().min(0),
    }),
  ),
  next: z.array(z.string()),
});

/* --- Save blob ------------------------------------------------------ */

export const regionSchema = z.object({
  id: z.string(),
  name: z.string(),
  subtitle: z.string(),
  homePlanet: z.string(),
  roster: z.array(z.string()).min(1),
  boss: z.string(),
});

export const planetUpgradeSchema = z.object({
  id: z.string(),
  name: z.string(),
  text: z.string(),
  costs: z.array(z.number().positive()).min(1),
});

export const saveSchema = z.object({
  version: z.literal(2),
  /** Meta currency, earned by winning levels, spent unlocking defences. */
  jewels: z.number().int().min(0),
  /** Structure ids bought with jewels. Starters aren't listed. */
  unlockedStructures: z.array(z.string()),
  cores: z.number().int().min(0),
  /** planetId → level 1..10 */
  planetLevels: z.record(z.string(), z.number().int().min(1).max(10)),
  /** planetId → chosen evolution branch ids */
  evolutions: z.record(z.string(), z.array(z.string())),
  unlockedPlanets: z.array(z.string()),
  /** levelId → stars 0..3 */
  stars: z.record(z.string(), z.number().int().min(0).max(3)),
  lastPlanet: z.string(),
});

export type SaveBlob = z.infer<typeof saveSchema>;

/**
 * v1 called the meta currency stardust and had no unlocks. Rather than throw
 * away someone's stars, carry them over and convert the balance.
 */
const v1Schema = z.object({
  version: z.literal(1),
  stardust: z.number().int().min(0),
  cores: z.number().int().min(0),
  planetLevels: z.record(z.string(), z.number().int()),
  evolutions: z.record(z.string(), z.array(z.string())),
  unlockedPlanets: z.array(z.string()),
  stars: z.record(z.string(), z.number().int()),
  lastPlanet: z.string(),
});

export function migrateSave(raw: unknown): SaveBlob | null {
  const v2 = saveSchema.safeParse(raw);
  if (v2.success) return v2.data;

  const v1 = v1Schema.safeParse(raw);
  if (!v1.success) return null;
  const { stardust, ...rest } = v1.data;
  return {
    ...rest,
    version: 2,
    // Stardust was ~65 per clear; jewels are ~2. Convert at that rate.
    jewels: Math.floor(stardust / 32),
    unlockedStructures: [],
  };
}

export function emptySave(): SaveBlob {
  return {
    version: 2,
    jewels: 0,
    unlockedStructures: [],
    cores: 0,
    planetLevels: { terra: 1 },
    evolutions: {},
    unlockedPlanets: ['terra'],
    stars: {},
    lastPlanet: 'terra',
  };
}
